import { useMemo } from "react";

interface ManufacturerOption {
  label: string;
  value: string;
}

interface SearchIndex {
  byPrefix: Map<string, ManufacturerOption[]>;
  all: ManufacturerOption[];
}

/**
 * Normalize string for search: lowercase and remove accents
 */
const normalizeString = (str: string): string => {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

/**
 * Build a prefix-based search index for fast lookups
 */
const buildSearchIndex = (manufacturers: ManufacturerOption[]): SearchIndex => {
  const byPrefix = new Map<string, ManufacturerOption[]>();
  
  manufacturers.forEach((manufacturer) => {
    const normalized = normalizeString(manufacturer.label);
    
    // Index by prefixes (first 1, 2, 3+ characters)
    for (let i = 1; i <= Math.min(normalized.length, 4); i++) {
      const prefix = normalized.substring(0, i);
      
      if (!byPrefix.has(prefix)) {
        byPrefix.set(prefix, []);
      }
      byPrefix.get(prefix)!.push(manufacturer);
    }
  });
  
  return {
    byPrefix,
    all: manufacturers
  };
};

/**
 * Fast manufacturer search hook using prefix-based indexing
 * Optimized for lists of 1000+ manufacturers
 */
export const useManufacturerSearch = (
  manufacturers: ManufacturerOption[],
  searchValue: string,
  maxResults: number = 3
): ManufacturerOption[] => {
  // Build search index (memoized - only rebuilt when manufacturers change)
  const searchIndex = useMemo(
    () => buildSearchIndex(manufacturers),
    [manufacturers]
  );
  
  // Perform search (memoized - only recalculated when search value or maxResults change)
  const results = useMemo(() => {
    const trimmedSearch = searchValue.trim();
    
    // If no search, return first N manufacturers
    if (!trimmedSearch) {
      return searchIndex.all.slice(0, maxResults);
    }
    
    const normalizedSearch = normalizeString(trimmedSearch);
    
    // Try to find by prefix first (O(1) lookup)
    const prefixResults = searchIndex.byPrefix.get(normalizedSearch);
    
    if (prefixResults && prefixResults.length > 0) {
      return prefixResults.slice(0, maxResults);
    }
    
    // Fallback: search within manufacturer names (contains search)
    const matchingManufacturers: ManufacturerOption[] = [];
    
    for (const manufacturer of searchIndex.all) {
      if (matchingManufacturers.length >= maxResults) {
        break;
      }
      
      const normalizedLabel = normalizeString(manufacturer.label);
      if (normalizedLabel.includes(normalizedSearch)) {
        matchingManufacturers.push(manufacturer);
      }
    }
    
    return matchingManufacturers;
  }, [searchIndex, searchValue, maxResults]);
  
  return results;
};
